import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatShort } from '../lib/format';
import { useConfirm } from './ConfirmDialog';
import { Button, Input, SectionTitle, Select, useToast } from './ui';

interface Registration {
  mailEnabled: boolean;
  link: { id: string; created_at: string; expires_at: string } | null;
}

/**
 * Zentraler Anmeldelink für die Mannschaftsgruppe.
 *
 * Wie beim persönlichen Link wird das Merkmal nur einmal angezeigt. Wer den
 * Link später noch einmal braucht, erzeugt einen neuen - der alte gilt dann
 * nicht mehr.
 */
export function RegistrationLinkCard({ teamId, seasonId }: { teamId: string; seasonId: string }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [tage, setTage] = useState('14');
  const [neu, setNeu] = useState<string | null>(null);
  const queryKey = ['sports-registration', teamId, seasonId];

  const registration = useQuery({
    queryKey,
    queryFn: () => api.get<Registration>(`/api/sports/teams/${teamId}/registration`, { seasonId }),
    enabled: Boolean(teamId && seasonId),
  });

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const erzeugen = useMutation({
    mutationFn: () => api.post<{ token: string; expiresAt: string }>(`/api/sports/teams/${teamId}/registration?seasonId=${seasonId}`, { days: Number(tage) }),
    onSuccess: async (result) => {
      setNeu(`${window.location.origin}/registrieren#${result.token}`);
      await qc.invalidateQueries({ queryKey });
      toast.show('Anmeldelink erzeugt');
    },
    onError: fehler('Anmeldelink konnte nicht erzeugt werden'),
  });

  const deaktivieren = useMutation({
    mutationFn: () => api.del(`/api/sports/teams/${teamId}/registration?seasonId=${seasonId}`),
    onSuccess: async () => {
      setNeu(null);
      await qc.invalidateQueries({ queryKey });
      toast.show('Anmeldelink deaktiviert');
    },
    onError: fehler('Anmeldelink konnte nicht deaktiviert werden'),
  });

  const neuErzeugen = async (): Promise<void> => {
    if (registration.data?.link) {
      const ok = await confirm({
        title: 'Neuen Anmeldelink erzeugen?',
        message: 'Der bisherige Anmeldelink wird ungültig. Wer ihn schon in der Gruppe gesehen hat, braucht den neuen Link.',
        confirmLabel: 'Ja, neu erzeugen',
        tone: 'danger',
      });
      if (!ok) return;
    }
    erzeugen.mutate();
  };
  const linkDeaktivieren = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Anmeldelink deaktivieren?',
      message: 'Der Link funktioniert danach nicht mehr. Wer sich noch nicht angemeldet hat, kann das nur über einen neuen Link.',
      confirmLabel: 'Ja, deaktivieren',
      tone: 'danger',
    });
    if (ok) deaktivieren.mutate();
  };

  const kopieren = async (adresse: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(adresse);
      toast.show('Link kopiert');
    } catch {
      toast.show('Bitte den Link von Hand kopieren', 'error');
    }
  };

  const link = registration.data?.link ?? null;

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-3">
      {confirmDialog}
      <SectionTitle
        title="Anmeldelink für die Gruppe"
        subtitle="Spieler wählen ihren Namen und bekommen den persönlichen Link per E-Mail"
      />
      {registration.data && !registration.data.mailEnabled && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Der E-Mail-Versand ist nicht eingerichtet. Ohne ihn kann sich niemand über den Anmeldelink registrieren –
          persönliche Links lassen sich weiterhin unten erzeugen und von Hand verteilen.
        </p>
      )}
      <p className="text-sm text-slate-600">
        Der Link zeigt Vorname und Initiale aller Kaderpersonen, die noch keinen persönlichen Link haben. Jeder Name lässt
        sich nur einmal wählen. Deaktiviere den Link, sobald sich alle angemeldet haben.
      </p>

      <p className="text-sm">
        {link ? (
          <>Aktiv bis <strong>{formatShort(link.expires_at)}</strong></>
        ) : (
          <span className="text-slate-500">Derzeit kein aktiver Anmeldelink</span>
        )}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Select aria-label="Gültigkeit" value={tage} onChange={(event) => setTage(event.target.value)} className="w-auto">
          <option value="3">3 Tage gültig</option>
          <option value="7">7 Tage gültig</option>
          <option value="14">14 Tage gültig</option>
          <option value="30">30 Tage gültig</option>
        </Select>
        <Button size="sm" loading={erzeugen.isPending} onClick={() => void neuErzeugen()}>
          {link ? 'Neuen Anmeldelink erzeugen' : 'Anmeldelink erzeugen'}
        </Button>
        {link && (
          <Button size="sm" variant="ghost" loading={deaktivieren.isPending} onClick={() => void linkDeaktivieren()}>
            Deaktivieren
          </Button>
        )}
      </div>

      {neu && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">Jetzt in die Gruppe stellen – der Link wird nur einmal angezeigt.</p>
          <div className="flex flex-wrap gap-2">
            <Input readOnly value={neu} aria-label="Anmeldelink" className="min-w-0 flex-1" />
            <Button size="sm" variant="secondary" onClick={() => void kopieren(neu)}>Kopieren</Button>
          </div>
        </div>
      )}
    </div>
  );
}
